import React, { useEffect, useState } from "react";
import Button from "../ui/Button";
import ColorInput from "../ui/ColorInput";
import UnitInput from "../ui/UnitInput";

interface ShadowEditorModalProps {
	value: string;
	onChange: (value: string) => void;
	onClose: () => void;
}

interface ShadowValues {
	hOffset: string;
	vOffset: string;
	blur: string;
	spread: string;
	color: string;
	inset: boolean;
}

// Helper function to parse box shadow value
const parseShadow = (value: string): ShadowValues => {
	// Default values
	const defaultValues: ShadowValues = {
		hOffset: "0px",
		vOffset: "0px",
		blur: "0px",
		spread: "0px",
		color: "rgba(0, 0, 0, 0.1)",
		inset: false,
	};

	// Handle 'none' value
	if (value === "none") {
		return defaultValues;
	}

	// Parse the shadow value
	const inset = value.includes("inset");
	const withoutInset = inset ? value.replace("inset", "").trim() : value;

	// Regular expression to match the shadow parts
	const parts = withoutInset.match(
		/(-?\d+[a-z%]+)\s+(-?\d+[a-z%]+)\s+(-?\d+[a-z%]+)\s+(-?\d+[a-z%]+)?\s*(rgba?\(.*\)|#[0-9a-f]+)?/i
	);

	if (parts) {
		return {
			hOffset: parts[1] || defaultValues.hOffset,
			vOffset: parts[2] || defaultValues.vOffset,
			blur: parts[3] || defaultValues.blur,
			spread: parts[4] || defaultValues.spread,
			color: parts[5] || defaultValues.color,
			inset,
		};
	}

	return defaultValues;
};

// Helper function to generate box shadow value
const generateShadow = (values: ShadowValues): string => {
	if (
		values.hOffset === "0px" &&
		values.vOffset === "0px" &&
		values.blur === "0px" &&
		values.spread === "0px"
	) {
		return "none";
	}

	const insetStr = values.inset ? "inset " : "";
	return `${insetStr}${values.hOffset} ${values.vOffset} ${values.blur} ${values.spread} ${values.color}`;
};

const ShadowEditorModal: React.FC<ShadowEditorModalProps> = ({
	value,
	onChange,
	onClose,
}) => {
	const [shadowValues, setShadowValues] = useState<ShadowValues>(
		parseShadow(value)
	);
	const [previewStyle, setPreviewStyle] = useState<React.CSSProperties>({});

	// Update preview when values change
	useEffect(() => {
		setPreviewStyle({
			boxShadow: generateShadow(shadowValues),
		});
	}, [shadowValues]);

	// Handle value changes
	const handleChange = (
		key: keyof ShadowValues,
		newValue: string | boolean
	) => {
		const updatedValues = { ...shadowValues, [key]: newValue };
		setShadowValues(updatedValues);
	};

	// Apply changes
	const handleApply = () => {
		const shadowString = generateShadow(shadowValues);
		onChange(shadowString);
		onClose();
	};

	// Reset to none
	const handleReset = () => {
		const defaultValues: ShadowValues = {
			hOffset: "0px",
			vOffset: "0px",
			blur: "0px",
			spread: "0px",
			color: "rgba(0, 0, 0, 0.1)",
			inset: false,
		};
		setShadowValues(defaultValues);
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-auto">
				<div className="flex justify-between items-center mb-4">
					<h2 className="text-xl font-bold">Box Shadow Editor</h2>
					<button
						className="p-1 rounded-full hover:bg-gray-100"
						onClick={onClose}>
						❌
					</button>
				</div>

				<div className="mb-6">
					<div
						className="border rounded-lg h-24 mb-4 flex items-center justify-center bg-white"
						style={previewStyle}>
						<div className="w-32 h-16 bg-gray-100 rounded border text-center flex items-center justify-center">
							Preview
						</div>
					</div>

					<div className="text-xs text-gray-500 mb-4">
						Current value:{" "}
						<code className="bg-gray-100 px-1 py-0.5 rounded">
							{generateShadow(shadowValues)}
						</code>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-4 mb-4">
					<UnitInput
						label="Horizontal Offset"
						value={shadowValues.hOffset}
						onChange={(val) => handleChange("hOffset", val)}
						units={["px", "rem", "em"]}
						min={-100}
						max={100}
					/>

					<UnitInput
						label="Vertical Offset"
						value={shadowValues.vOffset}
						onChange={(val) => handleChange("vOffset", val)}
						units={["px", "rem", "em"]}
						min={-100}
						max={100}
					/>

					<UnitInput
						label="Blur Radius"
						value={shadowValues.blur}
						onChange={(val) => handleChange("blur", val)}
						units={["px", "rem", "em"]}
						min={0}
						max={100}
					/>

					<UnitInput
						label="Spread Radius"
						value={shadowValues.spread}
						onChange={(val) => handleChange("spread", val)}
						units={["px", "rem", "em"]}
						min={-100}
						max={100}
					/>
				</div>

				<div className="mb-4">
					<ColorInput
						label="Shadow Color"
						value={shadowValues.color}
						onChange={(val) => handleChange("color", val)}
						withOpacity={true}
					/>
				</div>

				<div className="mb-6">
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={shadowValues.inset}
							onChange={(e) =>
								handleChange("inset", e.target.checked)
							}
							className="form-checkbox h-4 w-4"
						/>
						<span className="text-sm">Inset Shadow</span>
					</label>
				</div>

				<div className="flex justify-between pt-4 border-t">
					<Button variant="outline" onClick={handleReset}>
						Reset
					</Button>

					<div className="flex gap-2">
						<Button variant="outline" onClick={onClose}>
							Cancel
						</Button>

						<Button variant="primary" onClick={handleApply}>
							Apply
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ShadowEditorModal;
